import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useSelector, useDispatch } from "react-redux";
import moment from "moment";
import { Dropdown } from "react-bootstrap";
import {
	FiChevronLeft,
	FiChevronRight,
	FiEye,
	FiRefreshCcw,
	FiMoreHorizontal,
	FiTool,
} from "react-icons/fi";
import {
	plansService,
	accountsService,
	dashboardsService,
	employeesService,
} from "services";
import * as plansActions from "actions/plansActions";
import { showModal, hideModal } from "actions/modalActions";
import { isAdmin, isSupervisor } from "utils/utils";
import Loading from "components/Loading";
import EmployeeSelectSearch from "components/EmployeeSelectSearch/EmployeeSelectSearch";
import PreferenceEdit from "components/Preferences/PreferenceEditModal";
import CalendarViewEdit from "components/Calendars/CalendarViewEditModal";
import MobileCalendarGrid from "./MobileCalendarGrid";
import MobileCalendarDayList from "./MobileCalendarDayList";
import MobileCalendarKpis from "./MobileCalendarKpis";

const REQUIRED_DEFAULT_LABELS = new Set([
	"Horas trabajadas",
	"Horas objetivo",
	"Balance periodo",
	"Vacaciones a disfrutar año",
	"Vacaciones disfrutadas año",
	"Vacaciones pendientes",
]);

const isDefaultCalendarCounters = (rawCounters) => {
	if (!Array.isArray(rawCounters) || rawCounters.length === 0) return false;

	const inputLabels = new Set(rawCounters.map((item) => item.label));
	const allRequiredPresent = [...REQUIRED_DEFAULT_LABELS].every((label) =>
		inputLabels.has(label)
	);
	const noExtraLabels = [...inputLabels].every((label) =>
		REQUIRED_DEFAULT_LABELS.has(label)
	);

	return allRequiredPresent && noExtraLabels;
};

const findCounterValue = (rawCounters, property, name) => {
	const match = rawCounters.find(
		(c) => c[property] && c[property].name === name
	);
	return match ? match.value : null;
};

const buildCalendarKpis = (rawCounters, t) => {
	if (!Array.isArray(rawCounters) || rawCounters.length === 0) return [];

	if (isDefaultCalendarCounters(rawCounters)) {
		return [
			{
				label: t("calendar.workedHours"),
				value: findCounterValue(rawCounters, "account", "Horas trabajadas"),
			},
			{
				label: t("calendar.kpi.targetHours"),
				value: findCounterValue(rawCounters, "account", "Horas objetivo"),
			},
			{
				label: t("calendar.kpi.balance"),
				value: findCounterValue(rawCounters, "transaction_rule", "Balance"),
			},
		];
	}

	return rawCounters.map((c) => ({
		label: c.label,
		value: c.value,
	}));
};

const MobileCalendarView = () => {
	const dispatch = useDispatch();
	const { t } = useTranslation();
	const user = useSelector((state) => state.authReducer.user);
	const planViews = useSelector(
		(state) => state.plansReducer?.plan_views || []
	);
	const isStaff = isAdmin() || isSupervisor();

	const [year, setYear] = useState(new Date().getFullYear());
	const [currentMonth, setCurrentMonth] = useState(moment());
	const [selectedDay, setSelectedDay] = useState(
		moment().format("YYYY-MM-DD")
	);
	const [selectedEmployeeId, setSelectedEmployeeId] = useState(user?.id);
	const [selectedEmployeeName, setSelectedEmployeeName] = useState(
		user ? `${user.name || ""} ${user.surname || ""}`.trim() : ""
	);
	const [knownEmployee, setKnownEmployee] = useState(null);

	const [planAssignments, setPlanAssignments] = useState([]);
	const [absenceDays, setAbsenceDays] = useState([]);
	const [counters, setCounters] = useState([]);
	const [loading, setLoading] = useState(false);

	// leer query ?employee= si admin/supervisor
	useEffect(() => {
		if (!isStaff) return;
		const params = new URLSearchParams(window.location.search);
		const employeeFromQuery = params.get("employee");
		if (!employeeFromQuery) return;

		const employeeId = parseInt(employeeFromQuery, 10);
		if (Number.isNaN(employeeId)) return;

		(async () => {
			try {
				const res = await employeesService.getById(employeeId);
				if (res?.ok && res.id) {
					setSelectedEmployeeId(res.id);
					setSelectedEmployeeName(
						`${res.name} ${res.surname || ""}`.trim()
					);
					setKnownEmployee(res);
				}
			} catch (e) {
				console.warn("Empleado no encontrado o sin permisos:", e);
			}
		})();
	}, []);

	// plan_views sólo para el botón "Configurar vista"
	useEffect(() => {
		if (isStaff) {
			dispatch(plansActions.getPlanViews(true));
		}
	}, [dispatch, isStaff]);

	const fetchYearData = useCallback(async () => {
		if (!selectedEmployeeId) return;

		setLoading(true);

		const start = moment(`${year}-01-01`).startOf("year").format("YYYY-MM-DD");
		const end = moment(`${year}-01-01`).endOf("year").format("YYYY-MM-DD");

		try {
			const [absenceRes, plansRes, countersRes] = await Promise.all([
				dashboardsService.getEmployeeAbsenceCalendar(
					selectedEmployeeId,
					year
				),
				plansService.getPlanAssignments({
					employee_id: selectedEmployeeId,
					start,
					end,
				}),
				accountsService.getAccountAssignments({
					employee_id: selectedEmployeeId,
					start,
					end,
					my_calendar: true,
				}),
			]);

			const absList = absenceRes?.days || [];
			setAbsenceDays(Array.isArray(absList) ? absList : []);

			let plans = [];
			const pData = plansRes?.data;
			if (pData && pData.length > 0 && pData[0].plans?.length > 0) {
				plans = pData[0].plans;
			}
			setPlanAssignments(plans);

			if (countersRes?.ok && countersRes.data?.[0]?.counters) {
				setCounters(countersRes.data[0].counters);
			} else {
				setCounters([]);
			}
		} catch (e) {
			console.error("Error cargando calendario mobile:", e);
			setAbsenceDays([]);
			setPlanAssignments([]);
			setCounters([]);
		} finally {
			setLoading(false);
		}
	}, [selectedEmployeeId, year]);

	useEffect(() => {
		fetchYearData();
	}, [fetchYearData]);

	// si cambia el año, mover el mes a enero de ese año y reset selected day
	useEffect(() => {
		setCurrentMonth((prev) => {
			const prevYear = prev.year();
			if (prevYear === year) return prev;
			const today = moment();
			if (today.year() === year) return today.clone();
			return moment(`${year}-01-01`);
		});
		setSelectedDay((prev) => {
			const prevM = moment(prev, "YYYY-MM-DD");
			if (prevM.year() === year) return prev;
			const today = moment();
			if (today.year() === year) return today.format("YYYY-MM-DD");
			return `${year}-01-01`;
		});
	}, [year]);

	const calendarKpis = useMemo(() => buildCalendarKpis(counters, t), [counters, t]);

	const daysByKey = useMemo(() => {
		const map = new Map();
		absenceDays.forEach((d) => {
			if (d?.day) map.set(d.day, d);
		});
		return map;
	}, [absenceDays]);

	const plansByKey = useMemo(() => {
		const map = new Map();
		planAssignments.forEach((p) => {
			if (p?.day) map.set(p.day, p);
		});
		return map;
	}, [planAssignments]);

	const selectedDayInfo = useMemo(() => {
		return {
			dayData: daysByKey.get(selectedDay) || null,
			plan: plansByKey.get(selectedDay) || null,
		};
	}, [daysByKey, plansByKey, selectedDay]);

	const handlePrevMonth = () => {
		setCurrentMonth((prev) => prev.clone().subtract(1, "month"));
	};

	const handleNextMonth = () => {
		setCurrentMonth((prev) => prev.clone().add(1, "month"));
	};

	const handleEmployeeChange = (idStr, emp) => {
		if (!idStr) return;
		const id = Number(idStr);
		setSelectedEmployeeId(id);
		setSelectedEmployeeName(
			emp
				? `${emp.name} ${emp.surname || ""}`.trim()
				: selectedEmployeeName
		);
		setKnownEmployee(emp || null);
	};

	const closeModal = () => dispatch(hideModal());

	const openPreferenceModal = () => {
		dispatch(
			showModal(
				{
					modalProps: {
						open: true,
						title: t("calendar.requestShiftPreference"),
						style: { width: "500px" },
						content: <PreferenceEdit handleClose={closeModal} />,
						closeModal,
					},
					modalType: "edit",
				}
			)
		);
	};

	const openConfigureViewModal = () => {
		dispatch(
			showModal(
				{
					modalProps: {
						open: true,
						title: t("calendar.configureView"),
						style: { width: "400px" },
						content: (
							<CalendarViewEdit
								handleClose={closeModal}
								updateCalendarHeader={fetchYearData}
								plan_views={planViews}
							/>
						),
						closeModal,
					},
					modalType: "edit",
				}
			)
		);
	};

	const currentYear = new Date().getFullYear();
	const yearOptions = Array.from(
		{ length: currentYear + 1 - 2020 + 1 },
		(_, i) => currentYear + 1 - i
	);

	const canConfigureView = isStaff && planViews && planViews.length > 1;

	const isMyCalendar =
		Number(selectedEmployeeId) === Number(user?.id);
	const pageTitle = isMyCalendar
		? t("calendar.myCalendar")
		: t("calendar.calendarOf", { name: selectedEmployeeName });

	return (
		<div className="mobile-calendar-view">
			<div className="mobile-calendar-view__header">
				<h2 className="mobile-calendar-view__title">{pageTitle}</h2>

				<div className="mobile-calendar-view__actions">
					<button
						type="button"
						className="mobile-calendar-view__pref-btn"
						onClick={openPreferenceModal}
					>
						<FiRefreshCcw size={16} />
						<span>{t("calendar.requestShiftPreference")}</span>
					</button>

					<Dropdown align="end" className="mobile-calendar-view__menu">
						<Dropdown.Toggle
							as="button"
							className="mobile-calendar-view__menu-btn"
							aria-label={t("calendar.moreOptions")}
						>
							<FiMoreHorizontal size={18} />
						</Dropdown.Toggle>
						<Dropdown.Menu>
							{canConfigureView && (
								<Dropdown.Item
									as="button"
									onClick={openConfigureViewModal}
								>
									<FiTool className="mr5" /> {t("calendar.configureView")}
								</Dropdown.Item>
							)}
							{!canConfigureView && (
								<Dropdown.Item as="button" disabled>
									{t("calendar.noActionsAvailable")}
								</Dropdown.Item>
							)}
						</Dropdown.Menu>
					</Dropdown>
				</div>
			</div>

			<MobileCalendarKpis items={calendarKpis} />

			<div className="mobile-calendar-view__filters">
				{isStaff && (
					<div className="mobile-calendar-view__filter mobile-calendar-view__filter--employee">
						<EmployeeSelectSearch
							controlId="mobile_search_employee"
							multiple={false}
							allowClear={false}
							value={String(selectedEmployeeId)}
							onChange={handleEmployeeChange}
							fetchOptions={{ enabled: true }}
							placeholder={t("common.searchEmployee")}
							knownEmployee={
								knownEmployee ||
								(Number(selectedEmployeeId) === Number(user?.id)
									? user
									: null)
							}
							pinnedShortcut={
								Number(selectedEmployeeId) !== Number(user?.id)
									? {
											id: user?.id,
											label: t("calendar.myCalendar"),
											employee: user,
										}
									: null
							}
						/>
					</div>
				)}

				<div className="mobile-calendar-view__filter mobile-calendar-view__filter--year">
					<select
						className="mobile-calendar-view__year-select"
						value={year}
						onChange={(e) => setYear(Number(e.target.value))}
					>
						{yearOptions.map((y) => (
							<option key={y} value={y}>
								{y}
							</option>
						))}
					</select>
				</div>

				{canConfigureView && (
					<button
						type="button"
						className="mobile-calendar-view__eye-btn"
						onClick={openConfigureViewModal}
						aria-label={t("calendar.configureView")}
					>
						<FiEye size={18} />
					</button>
				)}
			</div>

			<div className="mobile-calendar-view__body">
				{loading ? (
					<div className="mobile-calendar-view__loading">
						<Loading />
					</div>
				) : (
					<>
						<div className="mobile-calendar-month">
							<div className="mobile-calendar-month__nav">
								<button
									type="button"
									className="mobile-calendar-month__arrow"
									onClick={handlePrevMonth}
									aria-label={t("calendar.previousMonth")}
								>
									<FiChevronLeft size={20} />
								</button>
								<span className="mobile-calendar-month__label">
									{currentMonth
										.format("MMMM YYYY")
										.replace(/^./, (c) => c.toUpperCase())}
								</span>
								<button
									type="button"
									className="mobile-calendar-month__arrow"
									onClick={handleNextMonth}
									aria-label={t("calendar.nextMonth")}
								>
									<FiChevronRight size={20} />
								</button>
							</div>

							<MobileCalendarGrid
								month={currentMonth}
								selectedDay={selectedDay}
								onSelectDay={setSelectedDay}
								daysByKey={daysByKey}
								plansByKey={plansByKey}
							/>
						</div>

						<MobileCalendarDayList
							day={selectedDay}
							dayData={selectedDayInfo.dayData}
							plan={selectedDayInfo.plan}
						/>
					</>
				)}
			</div>
		</div>
	);
};

export default MobileCalendarView;
